import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { Chip, Tooltip, Collapse, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, TextField, CircularProgress } from '@mui/material'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SourceIcon from '@mui/icons-material/Source'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ShortTextIcon from '@mui/icons-material/ShortText'
import SubjectIcon from '@mui/icons-material/Subject'
import TuneIcon from '@mui/icons-material/Tune'
import ReportSourceCitation from './ReportSourceCitation'
import MarkdownRenderer from 'components/MarkdownRenderer'

const ReportAnswerCard = ({
  answer,
  onSourceClick,
  onEdit,
  onRegenerate,
  isEditing,
  isRegenerating,
  regeneratingSubKey = null,
  canEdit = true,
  showSources = true
}) => {
  const {
    questionId,
    fieldLabel,
    question,
    answer: answerText,
    confidence,
    sources = [],
    answerType,
    enrichedSources = [],
    noInfoFound,
    qualityScore
  } = answer

  // Use fieldLabel if available, otherwise fall back to questionId
  const displayLabel = fieldLabel || questionId

  // Structured answer: answerType === 'structured' with subEnquiries map
  const isStructured = answerType === 'structured' && answer?.subEnquiries != null

  const [isExpanded, setIsExpanded] = useState(true)
  const wasRegenerating = useRef(false)

  // When regeneration completes, auto-expand to show the new answer
  useEffect(() => {
    if (wasRegenerating.current && !isRegenerating) {
      setIsExpanded(true)
    }
    wasRegenerating.current = isRegenerating
  }, [isRegenerating])

  const [editMode, setEditMode] = useState(false)
  const [editedAnswer, setEditedAnswer] = useState(answerText || '')
  const [regenerateAnchorEl, setRegenerateAnchorEl] = useState(null)
  const [customInstructionMode, setCustomInstructionMode] = useState(false)
  const [customInstruction, setCustomInstruction] = useState('')

  const handleEditClick = (e) => {
    e.stopPropagation()
    setEditedAnswer(answerText || '')
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditedAnswer(answerText || '')
  }

  const handleSaveEdit = () => {
    if (onEdit && editedAnswer.trim() !== answerText) {
      onEdit(questionId, editedAnswer.trim())
    }
    setEditMode(false)
  }

  const handleRegenerateClick = (e) => {
    e.stopPropagation()
    setRegenerateAnchorEl(e.currentTarget)
  }

  const handleRegenerateClose = () => {
    setRegenerateAnchorEl(null)
    setCustomInstructionMode(false)
    setCustomInstruction('')
  }

  const handleRegenerateOption = (feedback) => {
    if (feedback === 'custom') {
      setCustomInstructionMode(true)
    } else {
      if (onRegenerate) {
        onRegenerate(questionId, feedback, null)
      }
      handleRegenerateClose()
    }
  }

  const handleCustomRegenerate = () => {
    if (onRegenerate && customInstruction.trim()) {
      onRegenerate(questionId, 'custom', customInstruction.trim())
    }
    handleRegenerateClose()
  }

  // Use enrichedSources if available (they are full file objects)
  // Filter out any invalid sources and limit to 10
  const displaySources = (enrichedSources || []).filter(source => {
    if (typeof source === 'string') return source.length > 0
    return source && (source.name || source.displayName)
  }).slice(0, 10)

  // Gradient color scale based on confidence percentage
  const getConfidenceColor = (confidence) => {
    const percent = confidence * 100
    if (percent >= 85) return { color: '#059669', bgColor: '#D1FAE5', label: 'Excellent' } // Deep green
    if (percent >= 70) return { color: '#10B981', bgColor: '#ECFDF5', label: 'Good' } // Green
    if (percent >= 55) return { color: '#84CC16', bgColor: '#F7FEE7', label: 'Fair' } // Lime
    if (percent >= 40) return { color: '#EAB308', bgColor: '#FEF9C3', label: 'Moderate' } // Yellow
    if (percent >= 25) return { color: '#F97316', bgColor: '#FFEDD5', label: 'Low' } // Orange
    if (percent > 0) return { color: '#DC2626', bgColor: '#FEE2E2', label: 'Very Low' } // Red
    return { color: '#6B7280', bgColor: '#F3F4F6', label: 'No Data' } // Gray
  }

  const getConfidenceIcon = (confidence) => {
    const percent = confidence * 100
    if (percent >= 70) return <CheckCircleOutlineIcon fontSize="small" />
    if (percent >= 40) return <WarningAmberIcon fontSize="small" />
    if (percent > 0) return <InfoOutlinedIcon fontSize="small" />
    return <HelpOutlineIcon fontSize="small" />
  }

  const confidenceColor = getConfidenceColor(confidence)
  const confidenceIcon = getConfidenceIcon(confidence)
  const confidencePercent = Math.round(confidence * 100)
  const isLowConfidence = confidencePercent < 40

  const answerTextStr = typeof answerText === 'string' ? answerText : null
  const isNoDataFound = !isStructured && (
    noInfoFound || !answerTextStr ||
    answerTextStr.toLowerCase().includes('no information found') ||
    answerTextStr.toLowerCase().includes('not found in the documents')
  )

  return (
    <CardContainer isLowConfidence={isLowConfidence}>
      <QuestionSection onClick={() => setIsExpanded(!isExpanded)}>
        <QuestionContent>
          <QuestionIconWrapper isLowConfidence={isLowConfidence}>
            <QuestionAnswerIcon />
          </QuestionIconWrapper>
          <QuestionTextWrapper>
            <QuestionTitleRow>
              <FieldLabel>{displayLabel}</FieldLabel>
              <BadgeGroup>
                {qualityScore != null && (
                  <Tooltip title={`Answer quality: ${qualityScore}/100`} arrow>
                    <QualityBadge score={qualityScore}>
                      <span>Quality: {qualityScore}</span>
                    </QualityBadge>
                  </Tooltip>
                )}
                <Tooltip title={`${confidenceColor.label} confidence (${confidencePercent}%)`} arrow>
                  <ConfidenceBadge
                    confidenceColor={confidenceColor.color}
                    bgColor={confidenceColor.bgColor}
                    isLowConfidence={isLowConfidence}
                  >
                    {confidenceIcon}
                    <span>Confidence: {confidencePercent}%</span>
                  </ConfidenceBadge>
                </Tooltip>
              </BadgeGroup>
            </QuestionTitleRow>
            {question && question !== displayLabel && (
              <QuestionSubtext>{question}</QuestionSubtext>
            )}
          </QuestionTextWrapper>
        </QuestionContent>

        <QuestionActions>
          {isRegenerating ? (
            <LoadingIndicator>
              <CircularProgress size={20} sx={{ color: '#33becc' }} />
              <LoadingText>Regenerating...</LoadingText>
            </LoadingIndicator>
          ) : (
            <>
              {canEdit && !editMode && (
                <>
                  <Tooltip title="Edit answer" arrow>
                    <ActionIconButton
                      size="small"
                      onClick={handleEditClick}
                      disabled={isEditing}
                    >
                      <EditIcon fontSize="small" />
                    </ActionIconButton>
                  </Tooltip>
                  {/* Show icon for normal confidence, prominent button for low confidence */}
                  {isLowConfidence ? (
                    <RetryActionButton onClick={handleRegenerateClick} disabled={isEditing}>
                      <RefreshIcon fontSize="small" />
                      Retry
                    </RetryActionButton>
                  ) : (
                    <Tooltip title="Regenerate answer" arrow>
                      <ActionIconButton
                        size="small"
                        onClick={handleRegenerateClick}
                        disabled={isEditing}
                      >
                        <RefreshIcon fontSize="small" />
                      </ActionIconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </>
          )}
          <ExpandButton
            size="small"
            isExpanded={isExpanded}
            aria-label={isExpanded ? 'Collapse answer' : 'Expand answer'}
          >
            <ExpandMoreIcon />
          </ExpandButton>
        </QuestionActions>

        {/* Regenerate Menu */}
        <Menu
          anchorEl={regenerateAnchorEl}
          open={Boolean(regenerateAnchorEl)}
          onClose={handleRegenerateClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {!customInstructionMode ? (
            <>
              <MenuItem onClick={() => handleRegenerateOption('try_again')}>
                <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Try again</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleRegenerateOption('more_detailed')}>
                <ListItemIcon><SubjectIcon fontSize="small" /></ListItemIcon>
                <ListItemText>More detailed</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleRegenerateOption('more_concise')}>
                <ListItemIcon><ShortTextIcon fontSize="small" /></ListItemIcon>
                <ListItemText>More concise</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleRegenerateOption('custom')}>
                <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Custom instruction...</ListItemText>
              </MenuItem>
            </>
          ) : (
            <CustomInstructionContainer>
              <TextField
                autoFocus
                size="small"
                placeholder="e.g., Focus on fire safety compliance"
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customInstruction.trim()) {
                    handleCustomRegenerate()
                  }
                }}
                fullWidth
                sx={{ mb: 1 }}
              />
              <CustomInstructionActions>
                <CustomCancelButton onClick={handleRegenerateClose}>
                  Cancel
                </CustomCancelButton>
                <CustomSubmitButton
                  onClick={handleCustomRegenerate}
                  disabled={!customInstruction.trim()}
                >
                  Regenerate
                </CustomSubmitButton>
              </CustomInstructionActions>
            </CustomInstructionContainer>
          )}
        </Menu>
      </QuestionSection>

      <Collapse in={isExpanded} timeout={200}>
        <AnswerWrapper>
          {isStructured ? (
            <StructuredAnswerBlock
              subEnquiries={answer.subEnquiries}
              answerMap={typeof answer.answer === 'object' && answer.answer !== null ? answer.answer : {}}
              onSave={(subKey, value) => {
                const newMap = { ...(typeof answer.answer === 'object' && answer.answer !== null ? answer.answer : {}), [subKey]: value }
                if (onEdit) onEdit(questionId, newMap)
              }}
              isSaving={isEditing}
              questionId={questionId}
              onRegenerate={onRegenerate}
              regeneratingSubKey={regeneratingSubKey}
              canRegenerate={canEdit}
            />
          ) : (
            <AnswerSection isNoData={isNoDataFound && !editMode}>
              {editMode ? (
                <EditContainer>
                  <EditTextArea
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    placeholder="Enter your answer..."
                    rows={6}
                  />
                  <EditActions>
                    <CancelButton onClick={handleCancelEdit} disabled={isEditing}>
                      <CloseIcon fontSize="small" />
                      Cancel
                    </CancelButton>
                    <SaveButton onClick={handleSaveEdit} disabled={isEditing || !editedAnswer.trim()}>
                      {isEditing ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )}
                      Save
                    </SaveButton>
                  </EditActions>
                </EditContainer>
              ) : isNoDataFound ? (
                <NoDataContent>
                  <NoDataIcon>
                    <HelpOutlineIcon />
                  </NoDataIcon>
                  <NoDataTextWrapper>
                    <NoDataText>
                      No relevant information was found in the building documents for this question.
                    </NoDataText>
                    {canEdit && !isRegenerating && (
                      <NoDataRetryButton onClick={handleRegenerateClick} disabled={isEditing}>
                        <RefreshIcon fontSize="small" />
                        Try Again
                      </NoDataRetryButton>
                    )}
                  </NoDataTextWrapper>
                </NoDataContent>
              ) : (
                <AnswerContent>
                  <MarkdownRenderer content={answerTextStr} />
                </AnswerContent>
              )}
            </AnswerSection>
          )}

          {showSources && displaySources.length > 0 && !editMode && !isNoDataFound && (
            <SourcesSection>
              <SourcesHeader>
                <SourcesIcon>
                  <SourceIcon fontSize="small" />
                </SourcesIcon>
                <SourcesLabel>
                  Sources <SourcesCount>({displaySources.length})</SourcesCount>
                </SourcesLabel>
              </SourcesHeader>
              <SourcesList>
                {displaySources.map((source, index) => (
                  <ReportSourceCitation
                    key={source.id || source.fileId || index}
                    source={source}
                    onClick={() => {
                      if (typeof onSourceClick === 'function') {
                        onSourceClick(source)
                      } else if (source?.fileUrl) {
                        // No explicit handler (e.g. public share view) — open the
                        // pre-signed URL directly. noopener/noreferrer is required
                        // for window.open to shed opener access on the new tab.
                        window.open(source.fileUrl, '_blank', 'noopener,noreferrer')
                      }
                    }}
                  />
                ))}
              </SourcesList>
            </SourcesSection>
          )}
        </AnswerWrapper>
      </Collapse>
    </CardContainer>
  )
}

// Local component for rendering structured (multi-sub-enquiry) answers
const StructuredAnswerBlock = ({ subEnquiries, answerMap, onSave, isSaving, questionId, onRegenerate, regeneratingSubKey = null, canRegenerate = true }) => {
  const [drafts, setDrafts] = useState({})
  const [editingKey, setEditingKey] = useState(null)
  const [regenMenu, setRegenMenu] = useState({ anchorEl: null, subKey: null })
  const [customMode, setCustomMode] = useState(false)
  const [customText, setCustomText] = useState('')

  // Reset local edit state whenever the parent's answer map changes
  // (e.g. after a save creates a new report version, or after regeneration).
  // Without this, a user editing sub-enquiry 1.2 while another save is in flight
  // would overwrite the new server state with their stale draft on save.
  useEffect(() => {
    setDrafts({})
    setEditingKey(null)
  }, [answerMap])

  const handleEdit = (subKey) => {
    setDrafts(prev => ({ ...prev, [subKey]: answerMap[subKey] || '' }))
    setEditingKey(subKey)
  }

  const handleCancel = () => {
    setEditingKey(null)
  }

  const handleSave = (subKey) => {
    onSave(subKey, drafts[subKey] || '')
    setEditingKey(null)
  }

  const openRegenMenu = (e, subKey) => {
    setRegenMenu({ anchorEl: e.currentTarget, subKey })
    setCustomMode(false)
    setCustomText('')
  }

  const closeRegenMenu = () => {
    setRegenMenu({ anchorEl: null, subKey: null })
    setCustomMode(false)
    setCustomText('')
  }

  const pickRegen = (feedback) => {
    if (feedback === 'custom') {
      setCustomMode(true)
      return
    }
    if (onRegenerate && regenMenu.subKey) {
      onRegenerate(questionId, feedback, null, regenMenu.subKey)
    }
    closeRegenMenu()
  }

  const submitCustom = () => {
    if (onRegenerate && regenMenu.subKey && customText.trim()) {
      onRegenerate(questionId, 'custom', customText.trim(), regenMenu.subKey)
    }
    closeRegenMenu()
  }

  return (
    <StructuredBlock>
      {Object.entries(subEnquiries).map(([subKey, subText]) => {
        const isRegen = regeneratingSubKey === subKey
        return (
          <SubEnquiryRow key={subKey}>
            <SubEnquiryHeader>
              <SubEnquiryNum>{subKey}</SubEnquiryNum>
              <SubEnquiryText>{subText}</SubEnquiryText>
              {isRegen ? (
                <SubRegenLoading>
                  <CircularProgress size={14} sx={{ color: '#33becc' }} />
                  Regenerating…
                </SubRegenLoading>
              ) : editingKey !== subKey && (
                <SubActions>
                  <Tooltip title="Edit sub-answer" arrow>
                    <ActionIconButton size="small" onClick={() => handleEdit(subKey)} disabled={isSaving}>
                      <EditIcon fontSize="small" />
                    </ActionIconButton>
                  </Tooltip>
                  {canRegenerate && onRegenerate && (
                    <Tooltip title="Redo this answer with AI" arrow>
                      <ActionIconButton size="small" onClick={(e) => openRegenMenu(e, subKey)} disabled={isSaving}>
                        <AutoFixHighIcon fontSize="small" />
                      </ActionIconButton>
                    </Tooltip>
                  )}
                </SubActions>
              )}
            </SubEnquiryHeader>
            {editingKey === subKey ? (
              <SubEditContainer>
                <EditTextArea
                  value={drafts[subKey] || ''}
                  onChange={(e) => setDrafts(prev => ({ ...prev, [subKey]: e.target.value }))}
                  placeholder="Enter your answer..."
                  rows={4}
                />
                <EditActions>
                  <CancelButton onClick={handleCancel} disabled={isSaving}>
                    <CloseIcon fontSize="small" />
                    Cancel
                  </CancelButton>
                  <SaveButton onClick={() => handleSave(subKey)} disabled={isSaving}>
                    {isSaving ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : (
                      <SaveIcon fontSize="small" />
                    )}
                    Save
                  </SaveButton>
                </EditActions>
              </SubEditContainer>
            ) : (
              <SubAnswerValue empty={!answerMap[subKey]}>
                {answerMap[subKey]
                  ? <MarkdownRenderer content={String(answerMap[subKey])} />
                  : '(no answer yet)'}
              </SubAnswerValue>
            )}
          </SubEnquiryRow>
        )
      })}

      <Menu
        anchorEl={regenMenu.anchorEl}
        open={Boolean(regenMenu.anchorEl)}
        onClose={closeRegenMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!customMode ? (
          [
            <MenuItem key="try" onClick={() => pickRegen('try_again')}>
              <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Try again</ListItemText>
            </MenuItem>,
            <MenuItem key="det" onClick={() => pickRegen('more_detailed')}>
              <ListItemIcon><SubjectIcon fontSize="small" /></ListItemIcon>
              <ListItemText>More detailed</ListItemText>
            </MenuItem>,
            <MenuItem key="con" onClick={() => pickRegen('more_concise')}>
              <ListItemIcon><ShortTextIcon fontSize="small" /></ListItemIcon>
              <ListItemText>More concise</ListItemText>
            </MenuItem>,
            <MenuItem key="cus" onClick={() => pickRegen('custom')}>
              <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Custom instruction…</ListItemText>
            </MenuItem>
          ]
        ) : (
          <CustomInstructionContainer>
            <TextField
              autoFocus
              size="small"
              placeholder="e.g., Use the latest Fire Risk Assessment"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customText.trim()) {
                  submitCustom()
                }
              }}
              fullWidth
              sx={{ mb: 1 }}
            />
            <CustomInstructionActions>
              <CustomCancelButton onClick={closeRegenMenu}>Cancel</CustomCancelButton>
              <CustomSubmitButton onClick={submitCustom} disabled={!customText.trim()}>
                Regenerate
              </CustomSubmitButton>
            </CustomInstructionActions>
          </CustomInstructionContainer>
        )}
      </Menu>
    </StructuredBlock>
  )
}

export default ReportAnswerCard

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const CardContainer = styled.div`
  background: ${({ isLowConfidence }) => isLowConfidence ? '#FFFBEB' : '#ffffff'};
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.3s ease-out;
  border-left: ${({ isLowConfidence }) => isLowConfidence ? '3px solid #F97316' : 'none'};

  &:hover {
    background: ${({ isLowConfidence }) => isLowConfidence ? '#FEF3C7' : '#fafafa'};
  }
`

const QuestionSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  cursor: pointer;
  user-select: none;

  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const QuestionContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
`

const QuestionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${({ theme, isLowConfidence }) => isLowConfidence ? '#FED7AA' : `${theme.colors.primary}12`};
  border-radius: 10px;
  color: ${({ theme, isLowConfidence }) => isLowConfidence ? '#EA580C' : theme.colors.primary};
  flex-shrink: 0;

  svg {
    font-size: 20px;
  }

  @media (max-width: 600px) {
    width: 32px;
    height: 32px;

    svg {
      font-size: 18px;
    }
  }
`

const QuestionTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
  flex: 1;
  min-width: 0;
`

const QuestionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const FieldLabel = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  line-height: 1.4;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const QuestionSubtext = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.4;
  font-weight: 400;
`

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    justify-content: flex-end;
  }
`

const ConfidenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ confidenceColor }) => confidenceColor};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  border: ${({ isLowConfidence, confidenceColor }) => isLowConfidence ? `1px solid ${confidenceColor}40` : 'none'};

  svg {
    font-size: 14px;
  }

  @media (max-width: 600px) {
    padding: 3px 8px;
    font-size: 11px;

    svg {
      font-size: 12px;
    }
  }
`

const BadgeGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const QualityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${({ score }) =>
    score >= 80 ? '#ECFDF5' :
    score >= 50 ? '#FFF7ED' :
    '#FEF2F2'};
  color: ${({ score }) =>
    score >= 80 ? '#065F46' :
    score >= 50 ? '#9A3412' :
    '#991B1B'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;

  @media (max-width: 600px) {
    padding: 3px 8px;
    font-size: 11px;
  }
`

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #EA580C;
  background: #FFF7ED;
  border: 1px solid #FDBA74;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background: #FFEDD5;
    border-color: #F97316;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ExpandButton = styled(IconButton)`
  && {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => theme.colors.grey[200]};
    transition: all 0.2s ease;
    transform: ${({ isExpanded }) => isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primary}10;
    }
  }
`

const AnswerWrapper = styled.div`
  padding: 0 24px 20px;

  @media (max-width: 768px) {
    padding: 0 20px 16px;
  }
`

const AnswerSection = styled.div`
  padding: 20px;
  background: ${({ theme, isNoData }) =>
    isNoData
      ? `linear-gradient(135deg, ${theme.colors.grey[50] || '#F9FAFB'} 0%, #F3F4F6 100%)`
      : `linear-gradient(135deg, ${theme.colors.backgroundColor} 0%, #F8FAFC 100%)`};
  border-radius: 12px;
  border: 1px solid ${({ theme, isNoData }) =>
    isNoData ? '#E5E7EB' : theme.colors.lightGrey};

  @media (max-width: 600px) {
    padding: 16px;
    border-radius: 10px;
  }
`

const NoDataContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
    gap: 12px;
  }
`

const NoDataIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #E5E7EB;
  border-radius: 50%;
  color: #6B7280;
  flex-shrink: 0;

  svg {
    font-size: 20px;
  }
`

const NoDataTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`

const NoDataText = styled.p`
  color: ${({ theme }) => theme.colors.grey[200]};
  font-size: 14px;
  font-style: italic;
  margin: 0;
  line-height: 1.5;
`

const NoDataRetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 16px;
  }

  &:hover:not(:disabled) {
    background: #2BA8B9;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
  }
`

const AnswerContent = styled.div`
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 15px;
  line-height: 1.7;

  p {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: 12px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 6px;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.haiti};
  }

  @media (max-width: 600px) {
    font-size: 14px;

    ul, ol {
      padding-left: 20px;
    }
  }
`

const SourcesSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const SourcesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`

const SourcesIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const SourcesLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[200]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SourcesCount = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey[300] || '#9CA3AF'};
`

const SourcesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const ActionIconButton = styled(IconButton)`
  && {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => theme.colors.grey[200]};
    transition: all 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.colors.secondary};
      background: ${({ theme }) => theme.colors.secondary}15;
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.grey[300] || '#D1D5DB'};
    }
  }
`

const RetryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #F97316;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.3);

  svg {
    font-size: 16px;
  }

  &:hover:not(:disabled) {
    background: #EA580C;
    box-shadow: 0 4px 8px rgba(249, 115, 22, 0.4);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
    box-shadow: none;
  }
`

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: #33becc15;
  border-radius: 16px;
`

const LoadingText = styled.span`
  font-size: 12px;
  color: #33becc;
  font-weight: 500;
`

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EditTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  min-height: 120px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.secondary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[300] || '#9CA3AF'};
  }
`

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[200]};
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.lightGrey};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #2BA8B9;
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
  }
`

const CustomInstructionContainer = styled.div`
  padding: 12px 16px;
  min-width: 280px;
`

const CustomInstructionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const CustomCancelButton = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const CustomSubmitButton = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #2BA8B9;
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
  }
`

// Structured answer styled components
const StructuredBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
`

const SubEnquiryRow = styled.div`
  padding: 16px;
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.backgroundColor} 0%, #F8FAFC 100%)`};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SubEnquiryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const SubActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`

const SubRegenLoading = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 12px;
  color: #33becc;
  font-weight: 500;
  white-space: nowrap;
`

const SubEnquiryNum = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}12`};
  border-radius: 6px;
  padding: 2px 8px;
  flex-shrink: 0;
  margin-top: 1px;
`

const SubEnquiryText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.5;
  flex: 1;
`

const SubAnswerValue = styled.div`
  font-size: 14px;
  color: ${({ empty, theme }) => empty ? theme.colors.grey[200] : theme.colors.haiti};
  font-style: ${({ empty }) => empty ? 'italic' : 'normal'};
  line-height: 1.6;
  padding: 0 4px;
`

const SubEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`
